import dayjs from "dayjs";

// ==========================================
// 1. SETTINGS / DEFINITIONS CONFIGURATION
// ==========================================

export const CONVERTER_CONFIG = {
  // Default values for target ERP format
  defaults: {
    batCode: "001",
    branchID: "HNI",
    branchName: "Công ty Cổ phần Anvy - chi nhánh Hà Nội",
    custID: null,
    custName: null,
    fromSiteID: null,
    fromSiteName: null,
    geCpnyID: "AVM",
    geCpnyName: "CÔNG TY CỔ PHẦN ANVY",
    unit: "HOP",
    defaultLotSerNbr: "",
    defaultExpiryYears: 0,
  },

  // batCode mapped by bill type (1 = Nhập kho, 2 = Xuất kho)
  typeBatCodes: {
    1: "001",
    2: "002",
  },

  // Exact warehouse mappings (Nhanh Depot Name or ID -> ERP Code & Name)
  depotMappings: {
    "235655": { siteId: "AVMKK1407", siteName: "Kho Hàng Tốt sàn TMDT" },
    "Kho Hàng Tốt sàn TMDT": { siteId: "AVMKK1407", siteName: "Kho Hàng Tốt sàn TMDT" },

    "235659": { siteId: "AVMKK1402", siteName: "Kho hàng lỗi, cận date CN Hà Nội" },
    "Kho hàng lỗi, cận date CN Hà Nội": { siteId: "AVMKK1402", siteName: "Kho hàng lỗi, cận date CN Hà Nội" },

    "235664": { siteId: "AVMKK1403", siteName: "Kho chờ xử lý - CN Hà Nội" },
    "Kho chờ xử lý - CN Hà Nội": { siteId: "AVMKK1403", siteName: "Kho chờ xử lý - CN Hà Nội" },
  },

  // Substring keyword mappings to map warehouse name dynamically (disabled)
  depotSubstringMappings: [],
};

// ==========================================
// 2. PROCESSING MODULE FOR BILL/LIST DATA
// ==========================================

/**
 * Format a date string or timestamp to YYYY-MM-DDT00:00:00.
 * @param {string|number} dateVal
 * @returns {string}
 */
export const formatDateEnt = (dateVal) => {
  if (!dateVal) return "";
  const num = Number(dateVal);
  if (!Number.isNaN(num) && num > 0) {
    const d = num < 100000000000 ? dayjs(num * 1000) : dayjs(num);
    return d.isValid() ? d.format("YYYY-MM-DDT00:00:00") : "";
  }
  const d = dayjs(dateVal);
  return d.isValid() ? d.format("YYYY-MM-DDT00:00:00") : "";
};

/**
 * Parse expiration date (HSD) from a string of digits (DDMMYYYY or DDMMYY)
 * or fallback to dayjs parsing. Returns YYYY-MM-DDT00:00:00.
 * @param {string} hsd
 * @returns {string|null}
 */
export const parseExpiryDate = (hsd) => {
  if (!hsd || hsd === "-") return null;
  const cleaned = String(hsd).trim();
  if (/^\d{8}$/.test(cleaned)) {
    const dd = cleaned.substring(0, 2);
    const mm = cleaned.substring(2, 4);
    const yyyy = cleaned.substring(4);
    return `${yyyy}-${mm}-${dd}T00:00:00`;
  }
  if (/^\d{6}$/.test(cleaned)) {
    const dd = cleaned.substring(0, 2);
    const mm = cleaned.substring(2, 4);
    const yy = cleaned.substring(4);
    return `20${yy}-${mm}-${dd}T00:00:00`;
  }
  const d = dayjs(cleaned);
  if (d.isValid()) {
    return d.format("YYYY-MM-DDT00:00:00");
  }
  return null;
};

/**
 * Resolve depot ID and Name to the target ERP site ID and Name.
 * @param {string|number} depotId
 * @param {string} depotName
 * @returns {object} { siteId, siteName }
 */
export const getSiteInfo = (depotId, depotName) => {
  const name = String(depotName || "").trim();
  const id = String(depotId || "").trim();

  // 1. Exact name or ID match
  if (CONVERTER_CONFIG.depotMappings[name]) {
    return CONVERTER_CONFIG.depotMappings[name];
  }
  if (CONVERTER_CONFIG.depotMappings[id]) {
    return CONVERTER_CONFIG.depotMappings[id];
  }

  // 2. Keyword substring match
  const lowerName = name.toLowerCase();
  for (const mapping of CONVERTER_CONFIG.depotSubstringMappings) {
    if (lowerName.includes(mapping.keyword)) {
      return { siteId: mapping.siteId, siteName: mapping.siteName };
    }
  }

  // 3. Fallback to default ID-based format
  return {
    siteId: id ? `AVMKK${id}` : null,
    siteName: name || null,
  };
};

/**
 * Parses details of a raw item from Nhanh, returning its base inventory ID, name, unit, quantity,
 * and a parsed lot details object.
 * @param {object} item
 * @param {string} dateEnt Reference transaction date used for default expiration.
 * @returns {object}
 */
export const parseItemDetails = (item, dateEnt) => {
  const itemCode = String(item.productCode || item.code || item.id || "").trim();
  const rawName = String(item.productName || item.name || "").trim();
  const qty = Number(item.quantity || item.qty || 0);
  const unit = String(item.unit || CONVERTER_CONFIG.defaults.unit).trim();

  // Regex format e.g. AV003-0020626-10062029: (2 letters)(3 digits)-(lot)-(date digits)
  const singleFormatRegex = /^[A-Za-z]{2}\d{3}-.+-\d+$/;

  let invtID = itemCode;
  let lotSerNbr = CONVERTER_CONFIG.defaults.defaultLotSerNbr;
  let expDate = dateEnt
    ? dayjs(dateEnt).add(CONVERTER_CONFIG.defaults.defaultExpiryYears, "year").format("YYYY-MM-DDT00:00:00")
    : null;

  if (singleFormatRegex.test(itemCode)) {
    const blocks = itemCode.split("-");
    // Extract base SKU prefix directly (e.g. AV003)
    const firstBlock = blocks[0].trim();
    invtID = firstBlock.substring(0, 2) + "MTP03010" + firstBlock.slice(-3);
    lotSerNbr = blocks[1].trim();

    const rawHsd = blocks[2].trim();
    const parsedHsd = parseExpiryDate(rawHsd);
    if (parsedHsd) {
      expDate = parsedHsd;
    }
  } else {
    // If not composite SKU, look for potential lot / expiration fields
    if (item.lotSerNbr || item.lot) {
      lotSerNbr = String(item.lotSerNbr || item.lot).trim();
    }
    const rawExp = item.expDate || item.expiryDate || item.hsd;
    if (rawExp) {
      const parsedHsd = parseExpiryDate(rawExp);
      if (parsedHsd) {
        expDate = parsedHsd;
      }
    }
  }

  return {
    invtID,
    invtName: rawName,
    qty,
    unit,
    lot: {
      expDate,
      lotSerNbr,
      qty,
    },
  };
};

/**
 * Groups parsed items by invtID, accumulating quantity and merging lots.
 * @param {Array<object>} parsedItems
 * @returns {Array<object>}
 */
export const groupItemsByInvtID = (parsedItems) => {
  const grouped = {};
  let lineCount = 0;

  parsedItems.forEach((item) => {
    const { invtID, invtName, qty, unit, lot } = item;
    if (!grouped[invtID]) {
      grouped[invtID] = {
        invtID,
        invtName,
        lineRef: "",
        lots: [],
        qty: 0,
        unit,
      };
    }
    grouped[invtID].qty += qty;

    const existingLot = grouped[invtID].lots.find((l) => l.lotSerNbr === lot.lotSerNbr);
    if (existingLot) {
      existingLot.qty += lot.qty;
    } else {
      grouped[invtID].lots.push(lot);
    }
  });

  return Object.values(grouped).map((groupedItem) => {
    lineCount++;
    groupedItem.lineRef = String(lineCount).padStart(5, "0");
    return groupedItem;
  });
};

/**
 * Main function to convert raw Nhanh.vn `/bill/list` API response into ERP target format.
 * @param {object|string} apiResponse Raw API response object or string
 * @returns {object} ConversionResult
 */
export const convertBillsToTargetFormat = (apiResponse) => {
  let rawData = apiResponse;
  if (typeof apiResponse === "string") {
    try {
      rawData = JSON.parse(apiResponse);
    } catch (e) {
      console.error("Failed to parse API response as JSON:", e);
      return { data: [], errorMessage: "Invalid JSON response", statusCode: 0 };
    }
  }

  if (!rawData) {
    return { data: [], errorMessage: "Empty response", statusCode: 0 };
  }

  let bills = [];
  if (Array.isArray(rawData)) {
    bills = rawData;
  } else if (rawData.data) {
    if (Array.isArray(rawData.data)) {
      bills = rawData.data;
    } else if (typeof rawData.data === "object" && rawData.data !== null) {
      const nestedList = rawData.data.bills || rawData.data.list || rawData.data.items;
      if (Array.isArray(nestedList)) {
        bills = nestedList;
      } else {
        bills = Object.values(rawData.data).filter(
          (item) => item && typeof item === "object" && (item.id || item.code)
        );
      }
    }
  }

  const mergeableBills = [];
  const normalBills = [];

  bills.forEach((bill) => {
    if (!bill) return;
    const mode = Number(bill.mode);
    const reqId = bill.requirementBillId;
    if (mode === 3 && reqId && String(reqId).trim() !== "" && String(reqId) !== "0") {
      mergeableBills.push(bill);
    } else {
      normalBills.push(bill);
    }
  });

  const convertedNormalBills = normalBills.map((bill) => {
    const dateEnt = formatDateEnt(bill.createdDateTime || bill.date);

    const rawProducts = bill.products || bill.items || bill.productList || [];
    let productList = [];
    if (Array.isArray(rawProducts)) {
      productList = rawProducts;
    } else if (rawProducts && typeof rawProducts === "object") {
      productList = Object.values(rawProducts);
    }

    const parsedItems = productList.map((p) => parseItemDetails(p, dateEnt));
    const groupedItems = groupItemsByInvtID(parsedItems);

    const siteInfo = getSiteInfo(bill.depotId, bill.depotName || bill.toDepotName);

    return {
      batCode: CONVERTER_CONFIG.typeBatCodes[bill.type] || CONVERTER_CONFIG.defaults.batCode || "001",
      batNbr: String(bill.id || bill.code || ""),
      branchID: CONVERTER_CONFIG.defaults.branchID,
      branchName: CONVERTER_CONFIG.defaults.branchName,
      custID: CONVERTER_CONFIG.defaults.custID,
      custName: CONVERTER_CONFIG.defaults.custName,
      dateEnt: dateEnt,
      descr: bill.description || bill.note || "",
      fromSiteID: CONVERTER_CONFIG.defaults.fromSiteID,
      fromSiteName: CONVERTER_CONFIG.defaults.fromSiteName,
      geCpnyID: CONVERTER_CONFIG.defaults.geCpnyID,
      geCpnyName: CONVERTER_CONFIG.defaults.geCpnyName,
      items: groupedItems,
      reasonCD: bill.reasonCode || null,
      reasonDescr: bill.reasonDescription || null,
      salesName: bill.salesName || null,
      slsperID: bill.salesId || null,
      toSiteID: siteInfo.siteId,
      toSiteName: siteInfo.siteName,
    };
  });

  const groupedByReqId = {};
  mergeableBills.forEach((bill) => {
    const reqId = String(bill.requirementBillId).trim();
    if (!groupedByReqId[reqId]) {
      groupedByReqId[reqId] = [];
    }
    groupedByReqId[reqId].push(bill);
  });

  const convertedMergedBills = Object.keys(groupedByReqId).map((reqId) => {
    const group = groupedByReqId[reqId];

    // type 1: import (kho nhập), type 2: export (kho xuất)
    const importBill = group.find((b) => Number(b.type) === 1);
    const exportBill = group.find((b) => Number(b.type) === 2);

    const refBill = exportBill || importBill;
    const dateEnt = formatDateEnt(refBill.createdDateTime || refBill.date);

    const rawProducts = refBill.products || refBill.items || refBill.productList || [];
    let productList = [];
    if (Array.isArray(rawProducts)) {
      productList = rawProducts;
    } else if (rawProducts && typeof rawProducts === "object") {
      productList = Object.values(rawProducts);
    }

    const parsedItems = productList.map((p) => parseItemDetails(p, dateEnt));
    const groupedItems = groupItemsByInvtID(parsedItems);

    let fromSiteID = CONVERTER_CONFIG.defaults.fromSiteID;
    let fromSiteName = CONVERTER_CONFIG.defaults.fromSiteName;
    let toSiteID = null;
    let toSiteName = null;

    if (exportBill) {
      const fromSiteInfo = getSiteInfo(exportBill.depotId, exportBill.depotName || exportBill.toDepotName);
      fromSiteID = fromSiteInfo.siteId;
      fromSiteName = fromSiteInfo.siteName;
    } else if (importBill) {
      const fromSiteInfo = getSiteInfo(importBill.fromDepotId, importBill.fromDepotName);
      fromSiteID = fromSiteInfo.siteId;
      fromSiteName = fromSiteInfo.siteName;
    }

    if (importBill) {
      const toSiteInfo = getSiteInfo(importBill.depotId, importBill.depotName || importBill.toDepotName);
      toSiteID = toSiteInfo.siteId;
      toSiteName = toSiteInfo.siteName;
    } else if (exportBill) {
      const toSiteInfo = getSiteInfo(exportBill.toDepotId, exportBill.toDepotName);
      toSiteID = toSiteInfo.siteId;
      toSiteName = toSiteInfo.siteName;
    }

    const exportId = exportBill?.id || exportBill?.code || "";
    const importId = importBill?.id || importBill?.code || "";
    const batNbr = `${exportId}-${importId}`;

    return {
      batCode: "003",
      batNbr: batNbr,
      branchID: CONVERTER_CONFIG.defaults.branchID,
      branchName: CONVERTER_CONFIG.defaults.branchName,
      custID: CONVERTER_CONFIG.defaults.custID,
      custName: CONVERTER_CONFIG.defaults.custName,
      dateEnt: dateEnt,
      descr: exportBill?.description || exportBill?.note || importBill?.description || importBill?.note || "",
      fromSiteID: fromSiteID,
      fromSiteName: fromSiteName,
      geCpnyID: CONVERTER_CONFIG.defaults.geCpnyID,
      geCpnyName: CONVERTER_CONFIG.defaults.geCpnyName,
      items: groupedItems,
      reasonCD: exportBill?.reasonCode || importBill?.reasonCode || null,
      reasonDescr: exportBill?.reasonDescription || importBill?.reasonDescription || null,
      salesName: exportBill?.salesName || importBill?.salesName || null,
      slsperID: exportBill?.salesId || importBill?.salesId || null,
      toSiteID: toSiteID,
      toSiteName: toSiteName,
    };
  });

  return {
    data: [...convertedNormalBills, ...convertedMergedBills],
    errorMessage: rawData.errorMessage || rawData.message || "",
    statusCode: rawData.code !== undefined ? rawData.code : 1,
  };
};
